// Legacy TimeInput (components/form/TimeInput.vue): an outlined q-input over @jobinsjp/vue3-datepicker `type="time"`,
// format `h:mm a`. Measured live on the legacy back office (Business Hours, 2026-10-14):
// - Focusing or clicking the field opens a 226×226 panel (224 content and a 1 px #e8e8e8 border, square corners, shadow
//   0 6px 12px rgba(0,0,0,.175)) beside the field as the date picker's `getRelativePosition` places it: under the field
//   when the field's middle is in the top half of the window, else over it; on its left edge when the field's middle is
//   in the left half, else on its right edge (Business Hours' fields sit right, so their panel hangs from the right edge;
//   Add Automatic Discount's Start Time sits low and left, so its panel opens above, from the left edge; B693). A panel
//   that fits neither way is pinned to the window's edge instead, and the panel is placed again whenever the field's
//   scroll parent scrolls or the window resizes. The panel hangs from the field's box: TimeInput's q-input is
//   hide-bottom-space, so its error line never moves it.
// - Three columns (74/75/75 px, split by 1 px rules): hours 12, 01 … 11; minutes 00 … 59; AM, PM. Items are 32 px high,
//   12 px Helvetica Neue in #73879c, #f3f9fe on hover; the chosen one is bold #1284e7. An empty field marks 12 / 00 / AM.
// - Each column scrolls its chosen item to the top (a 192 px tail lets the last ones get there); its own 6 px scrollbar
//   shows only while the pointer is over the column.
// - A pick sets the time at once ("9:00 am" after picking 09 on an empty field) and leaves the panel open; only a click
//   outside closes it (Escape does not). The box also takes typing: on Enter or on leaving it the text is read as a time,
//   an unreadable one goes back to the last time, and an emptied box clears it.
import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type UIEvent } from 'react';
import { Popover } from 'radix-ui';
import { FieldFrame } from './field.tsx';

/** A wall-clock time of day in minutes after midnight. */
type Minutes = number;

const HOURS = ['12', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11'] as const;
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
const MERIDIEM = ['AM', 'PM'] as const;
const ITEM = 32;

/**
 * The panel's size as the date picker's `getPopupElementSize` measures it: 226 wide (224 content and a 1 px border each
 * side), and 228 high, as .mx-datepicker-popup's 1 px top and bottom margins count too. The top margin also draws the
 * panel 1 px below where it is placed.
 */
const PANEL = { width: 226, height: 228 } as const;
const MARGIN_TOP = 1;

/**
 * Where the panel opens from the field's box, as vue3-datepicker's `getRelativePosition` decides it: pinned 1 px from the
 * window's left edge when it fits neither from the box's left edge nor to its right edge, else by the window's halves; and
 * pinned to the window's bottom when it fits neither above nor below the box, else by the window's halves. The offsets
 * are what the popover adds to its side and alignment to land where legacy's panel does.
 */
interface TimePanelPlacement {
  readonly side: 'top' | 'bottom';
  readonly align: 'start' | 'end';
  readonly sideOffset: number;
  readonly alignOffset: number;
}

interface Box { readonly left: number; readonly top: number; readonly right: number; readonly bottom: number; readonly width: number; readonly height: number }

export function timePanelPlacement(field: Box, window: { readonly width: number; readonly height: number }, panel: { readonly width: number; readonly height: number } = PANEL): TimePanelPlacement {
  const pinLeft = window.width - field.left < panel.width && field.right < panel.width;
  const pinBottom = field.top <= panel.height && window.height - field.bottom <= panel.height;
  return {
    side: pinBottom || field.top + field.height / 2 <= window.height / 2 ? 'bottom' : 'top',
    align: pinLeft || field.left + field.width / 2 <= window.width / 2 ? 'start' : 'end',
    // Legacy places the pinned panel at the window's height less its measured 228, and its top margin draws it 1 px lower
    // (226 px from window height - 227 to window height - 1); otherwise the margin is the 1 px gap from the box.
    sideOffset: pinBottom ? window.height - panel.height + MARGIN_TOP - field.bottom : MARGIN_TOP,
    alignOffset: pinLeft ? 1 - field.left : 0,
  };
}

/** Where the panel opens from this field's box in the window as it is now. */
function placementOf(box: HTMLElement): TimePanelPlacement {
  return timePanelPlacement(box.getBoundingClientRect(), { width: document.documentElement.clientWidth, height: document.documentElement.clientHeight });
}

/** The element whose scrolling moves the field, as the date picker's `getScrollParent` finds it (null: the window). */
function scrollParent(node: HTMLElement | null): HTMLElement | null {
  if (node === null || node === document.body) return null;
  const style = getComputedStyle(node);
  return /(auto|scroll)/.test(style.overflow + style.overflowY + style.overflowX) ? node : scrollParent(node.parentElement);
}

/**
 * Reads a typed time the way legacy's moment parse (formats `HH:mm:ss`, `hh:mm:ss a`, `hh:mm a`) accepts it: "9:00 am",
 * "09:00 PM", "9:00pm", "21:00", "21:00:00", "9 pm". Null when it is not a time.
 */
export function parseTimeText(text: string): Minutes | null {
  const match = /^\s*(\d{1,2})(?::(\d{2}))?(?::(\d{2}))?\s*([ap])?\.?m?\.?\s*$/i.exec(text);
  if (match === null) return null;
  const hour = Number(match[1]);
  const minute = match[2] === undefined ? 0 : Number(match[2]);
  if (minute > 59 || (match[3] !== undefined && Number(match[3]) > 59)) return null;
  const suffix = match[4]?.toLowerCase();
  if (suffix === undefined) return match[2] === undefined || hour > 23 ? null : hour * 60 + minute;
  if (hour < 1 || hour > 12) return null;
  return ((hour % 12) + (suffix === 'p' ? 12 : 0)) * 60 + minute;
}

/** How a picked or typed time is written: legacy TimeInput's `h:mm a` ("9:00 am"), or the timecard editor's `hh:mm a` ("09:00 am"). */
export type TimeTextFormat = 'h:mm a' | 'hh:mm a';

/** Legacy's `h:mm a`: "9:00 am", "12:30 pm" (or `hh:mm a`: "09:00 am"). */
export function formatTimeText(minutes: Minutes, format: TimeTextFormat = 'h:mm a'): string {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const h12 = String(hour % 12 === 0 ? 12 : hour % 12);
  return `${format === 'hh:mm a' ? h12.padStart(2, '0') : h12}:${String(minute).padStart(2, '0')} ${hour < 12 ? 'am' : 'pm'}`;
}

interface ColumnProps {
  readonly label: string;
  readonly items: readonly string[];
  readonly chosen: number;
  readonly onPick: (index: number) => void;
}

/** One scrolling column with legacy's hover-only scrollbar. */
function Column({ label, items, chosen, onPick }: ColumnProps) {
  const ref = useRef<HTMLDivElement>(null);
  const first = useRef(true);
  const [thumb, setThumb] = useState({ top: 0, height: 0 });
  const measure = (el: HTMLDivElement) => {
    const { clientHeight, scrollHeight, scrollTop } = el;
    setThumb(scrollHeight <= clientHeight ? { top: 0, height: 0 } : { top: (scrollTop / scrollHeight) * clientHeight, height: (clientHeight * clientHeight) / scrollHeight });
  };
  // The chosen item goes to the top: at once when the panel opens, smoothly after a pick.
  useLayoutEffect(() => {
    const el = ref.current;
    if (el === null) return;
    const top = chosen * ITEM;
    if (first.current || typeof el.scrollTo !== 'function') el.scrollTop = top;
    else el.scrollTo({ top, behavior: 'smooth' });
    first.current = false;
    measure(el);
  }, [chosen]);
  return (
    <div className="group relative h-full flex-1 border-l border-[var(--time-picker-border)] first:border-l-0" data-part="time-column">
      <div ref={ref} onScroll={(e: UIEvent<HTMLDivElement>) => measure(e.currentTarget)} className="h-full overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <ul role="listbox" aria-label={label} className="m-0 list-none p-0 after:block after:h-[192px] after:content-['']">
          {items.map((text, i) => (
            <li
              key={text} role="option" aria-selected={i === chosen} onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(i)}
              className={`h-8 cursor-pointer text-center leading-8 hover:bg-[var(--time-picker-hover)] ${
                i === chosen ? 'font-bold text-[var(--time-picker-active)]' : 'hover:text-[var(--time-picker-text-hover)]'
              }`}
            >
              {text}
            </li>
          ))}
        </ul>
      </div>
      {thumb.height > 0 ? (
        <div aria-hidden className="pointer-events-none absolute top-[2px] right-[2px] bottom-[2px] w-[6px] rounded opacity-0 transition-opacity duration-[240ms] ease-out group-hover:opacity-100">
          <div className="absolute w-full rounded bg-[rgb(144_147_153/0.3)]" style={{ top: thumb.top, height: thumb.height }} />
        </div>
      ) : null}
    </div>
  );
}

interface TimeFieldProps {
  readonly label: string;
  /** The time as legacy shows it ("9:00 am"), or '' for none. */
  readonly value: string;
  /** Always legacy's `h:mm a` ("9:00 am"), or '' when the box is emptied. */
  readonly onValueChange: (value: string) => void;
  /**
   * Every time the user sets a time: a typed time committed (Enter or leaving the box) or a pick from the list, even one
   * equal to the value shown; never on a bare focus, blur or reformat. Legacy's time fields emit from their setter on
   * any such set, so a caller that must tell an untouched time from a re-entered one listens here (B1854).
   */
  readonly onSet?: (() => void) | undefined;
  readonly 'aria-label'?: string;
  readonly dense?: boolean;
  readonly disabled?: boolean;
  readonly error?: string | undefined;
  readonly frameClassName?: string;
  /**
   * `hh:mm a` for the timecard editor (legacy TimecardClockInTime and the break times: vue-datepicker `hh:mm a`, the same
   * panel, measured 2026-10-15 by lane D4); `h:mm a` (the default) for TimeInput.
   */
  readonly format?: TimeTextFormat;
  /**
   * What an unreadable typed time does on Enter or leaving the box: TimeInput puts the last time back ('revert', the
   * default); the discount schedules' StartTime and EndTime (moment's "Invalid date", reset on blur) clear it ('clear', B693).
   */
  readonly unreadable?: 'revert' | 'clear' | undefined;
  /**
   * Close the panel when a typed change is committed (Enter or leaving the box), as the discount schedules' StartTime and
   * EndTime q-inputs do with `@change="timePanelOpen = false"` (B708). TimeInput (the default) closes only on a click outside.
   */
  readonly closeOnTypedChange?: boolean | undefined;
}

/** Legacy TimeInput: an outlined time box with the hour / minute / AM-PM picker. */
export function TimeField({ label, value, onValueChange, onSet, 'aria-label': ariaLabel, dense, disabled, error, frameClassName, format = 'h:mm a', unreadable = 'revert', closeOnTypedChange = false }: TimeFieldProps) {
  const id = useId();
  // The panel hangs from the field's box, not the frame: legacy's q-input is hide-bottom-space, so its error line never
  // moves the panel.
  const anchor = useRef<HTMLDivElement>(null);
  const frame = useRef<HTMLDivElement>(null);
  const [open, setOpenState] = useState(false);
  const [placement, setPlacement] = useState<TimePanelPlacement>({ side: 'bottom', align: 'end', sideOffset: 1, alignOffset: 0 });
  const setOpen = (next: boolean) => {
    if (next && !open && anchor.current !== null) setPlacement(placementOf(anchor.current));
    setOpenState(next);
  };
  // Legacy places the panel again whenever the field's scroll parent scrolls or the window resizes, once a frame.
  useEffect(() => {
    if (!open) return undefined;
    const target: HTMLElement | Window = scrollParent(anchor.current) ?? window;
    let frameId: number | null = null;
    const onMove = () => {
      if (frameId !== null) return;
      frameId = requestAnimationFrame(() => { frameId = null; if (anchor.current !== null) setPlacement(placementOf(anchor.current)); });
    };
    target.addEventListener('scroll', onMove);
    window.addEventListener('resize', onMove);
    return () => {
      target.removeEventListener('scroll', onMove);
      window.removeEventListener('resize', onMove);
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
  }, [open]);
  const [focused, setFocused] = useState(false);
  // What the box shows while it is typed into; null when it shows the value.
  const [typed, setTyped] = useState<string | null>(null);
  const current = parseTimeText(value);
  const at = current ?? 0;
  const hour = Math.floor(at / 60);

  useEffect(() => {
    if (disabled === true) setOpenState(false);
  }, [disabled]);

  // A native change event: the box's text differs from what it showed, committed by Enter or by leaving it.
  const commitTyped = () => {
    if (closeOnTypedChange && typed !== null && typed !== value) setOpen(false);
    commit();
  };
  const commit = () => {
    if (typed === null) return;
    setTyped(null);
    if (typed.trim() === '') {
      if (value !== '') onValueChange('');
      return;
    }
    const read = parseTimeText(typed);
    if (read === null) { if (unreadable === 'clear' && value !== '') onValueChange(''); return; }
    onSet?.();
    if (formatTimeText(read, format) !== value) onValueChange(formatTimeText(read, format));
  };
  const pick = (minutes: Minutes) => {
    setTyped(null);
    onSet?.();
    onValueChange(formatTimeText(minutes, format));
  };
  const close = () => {
    commit();
    setOpen(false);
  };

  return (
    <Popover.Root open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
      <div ref={frame} className={frameClassName}>
        <FieldFrame
          id={id} label={label} floated={focused || open || (typed ?? value) !== ''} focused={focused || open} error={error} dense={dense} disabled={disabled} disabledLook="time-input"
          wrapBox={(box) => <Popover.Anchor asChild><div ref={anchor}>{box}</div></Popover.Anchor>}
        >
          <div className={`flex w-full items-center ${dense === true ? 'pt-3' : 'pt-4'}`}>
            <input
              id={id}
              value={typed ?? value}
              disabled={disabled}
              aria-label={ariaLabel}
              // A text box that opens the hour, minute and AM/PM listboxes: a combobox, the only role that may carry
              // aria-expanded on an input (axe aria-allowed-attr failed the discount and break dialogs without it).
              role="combobox"
              aria-haspopup="listbox"
              aria-expanded={open}
              aria-invalid={error !== undefined && error !== '' ? true : undefined}
              autoComplete="off"
              onChange={(e) => setTyped(e.target.value)}
              onFocus={() => { setFocused(true); setOpen(true); }}
              onClick={() => setOpen(true)}
              onBlur={() => { setFocused(false); commitTyped(); }}
              onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') { e.preventDefault(); commitTyped(); } }}
              className="w-full min-w-0 bg-transparent text-[14px] text-text-1 outline-none focus-visible:outline-none"
            />
          </div>
        </FieldFrame>
      </div>
      <Popover.Portal>
        <Popover.Content
          align={placement.align} side={placement.side} sideOffset={placement.sideOffset} alignOffset={placement.alignOffset} avoidCollisions={false} data-testid="time-picker"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          // Legacy's panel ignores Escape; only a click outside closes it.
          onEscapeKeyDown={(e) => e.preventDefault()}
          onFocusOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => { if (frame.current?.contains(e.target as Node) === true) e.preventDefault(); }}
          style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}
          className="z-[3000] flex h-[226px] w-[226px] border border-[var(--time-picker-border)] bg-[var(--time-picker-background)] text-[12px] tracking-normal text-[var(--time-picker-text)] shadow-[0_6px_12px_rgba(0,0,0,0.175)]"
        >
          <Column label="Hour" items={HOURS} chosen={hour % 12} onPick={(i) => pick((i + (hour >= 12 ? 12 : 0)) * 60 + (at % 60))} />
          <Column label="Minute" items={MINUTES} chosen={at % 60} onPick={(i) => pick(hour * 60 + i)} />
          <Column label="AM or PM" items={MERIDIEM} chosen={hour >= 12 ? 1 : 0} onPick={(i) => pick(((hour % 12) + i * 12) * 60 + (at % 60))} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
